import React from 'react';
import { Users_Constraint, Users_Update_Column, Usertocustomers_Constraint, Usertocustomers_Update_Column, Usertoroles_Constraint, Usertoroles_Update_Column } from '@gql/base-types';
import { useInsertDriverMutation } from '../queries/insertDriver.mutation.generated';
import { useUpdateDriverMutation } from '../queries/updateDriver.mutation.generated';
import { GetDriversDocument } from '../queries/getDrivers.query.generated';
import { useApolloClient } from '@apollo/client';
import { useRooftop, useUser } from '@store';
import { condensedCase } from '@services/formatService';
import {
  type ImportRow,
  parseCsvData,
  formatDriverImportError,
  findDuplicateIndices,
} from '../utils/driverImportUtils';

export type { ImportRow } from '../utils/driverImportUtils';
export type { RowStatus } from '../utils/driverImportUtils';

const useDriverImport = () => {
  const [rows, setRows] = React.useState<ImportRow[]>([]);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [isComplete, setIsComplete] = React.useState(false);

  const [insertDriver] = useInsertDriverMutation();
  const [updateDriver] = useUpdateDriverMutation();
  const apolloClient = useApolloClient();
  const rooftop = useRooftop();
  const user = useUser();

  const parseCsv = (results: any) => {
    const validated = parseCsvData(results);
    if (validated.length === 0) return;
    setRows(validated);
    setIsComplete(false);
  };

  const processAll = async () => {
    const currentCustomerId = rooftop?.id || user?.profile?.customer_id;
    if (!currentCustomerId) return;

    setIsProcessing(true);

    // Fetch existing drivers to detect duplicates before inserting
    let existingEmails = new Set<string>();
    try {
      const { data: existingData } = await apolloClient.query({
        query: GetDriversDocument,
        variables: { customerId: currentCustomerId },
        fetchPolicy: 'network-only',
      });
      existingEmails = new Set(
        (existingData?.drivers || [])
          .map((d: any) => d.user?.email?.toLowerCase())
          .filter(Boolean)
      );
    } catch (err) {
      console.warn('Could not fetch existing drivers for duplicate check:', err);
    }

    // Mark rows with existing emails as errors before processing
    const pendingRows = rows.filter(r => r.status === 'pending');
    const duplicateIndices = findDuplicateIndices(pendingRows, existingEmails);

    if (duplicateIndices.size > 0) {
      setRows(prev =>
        prev.map(r =>
          duplicateIndices.has(r.index)
            ? { ...r, status: 'error', error: 'A driver with this email already exists' }
            : r
        )
      );
    }

    const validIndices = pendingRows
      .filter(r => !duplicateIndices.has(r.index))
      .map(r => r.index);

    const TIMEOUT_MS = 15000;

    await Promise.allSettled(
      validIndices.map(idx => {
        setRows(prev => prev.map(r => (r.index === idx ? { ...r, status: 'processing' } : r)));

        const markError = (message: string) => {
          setRows(prev =>
            prev.map(r => (r.index === idx && r.status !== 'success' ? { ...r, status: 'error', error: message } : r))
          );
        };

        const timeoutPromise = new Promise<void>(resolve => {
          setTimeout(() => {
            markError('Request timed out');
            resolve();
          }, TIMEOUT_MS);
        });

        const work = (async () => {
          try {
            const row = rows.find(r => r.index === idx)!;
            const driverInput = {
              status: 'offline',
              tax_class: 'lite',
              driver_app_version: '3.0.0',
              config: { includePortalLinkInWelcomeEmail: false },
              user: {
                data: {
                  display_name: row.name || null,
                  email: row.email || null,
                  phone: condensedCase(row.phone) || null,
                  active: false,
                  default_role: 'dealer',
                  customer_id: currentCustomerId,
                  users_usertocustomers: {
                    data: [
                      {
                        customer_id: currentCustomerId,
                        created_by: user?.profile?.email,
                        updated_by: user?.profile?.email,
                        created_at: 'now()',
                        updated_at: 'now()',
                      },
                    ],
                    on_conflict: {
                      constraint: Usertocustomers_Constraint.UsertocustomersUserIdCustomerIdKey,
                      update_columns: [Usertocustomers_Update_Column.UpdatedAt],
                    },
                  },
                  users_usertoroles: {
                    data: [
                      {
                        role_id: 2,
                        created_by: user?.profile?.email,
                        updated_by: user?.profile?.email,
                        created_at: 'now()',
                        updated_at: 'now()',
                      },
                      {
                        role_id: 4,
                        created_by: user?.profile?.email,
                        updated_by: user?.profile?.email,
                        created_at: 'now()',
                        updated_at: 'now()',
                      },
                    ],
                    on_conflict: {
                      constraint: Usertoroles_Constraint.UsertorolesUserIdRoleIdKey,
                      update_columns: [Usertoroles_Update_Column.UpdatedAt],
                    },
                  },
                },
                on_conflict: {
                  constraint: Users_Constraint.UsersEmailKey,
                  update_columns: [Users_Update_Column.Phone, Users_Update_Column.Active, Users_Update_Column.UpdatedAt],
                },
              },
            };

            const result = await insertDriver({
              variables: { driverInput },
              errorPolicy: 'all',
            });

            if (result.errors && result.errors.length > 0) {
              throw result.errors[0];
            }

            const createdDriver = result.data?.insert_drivers_one;
            if (!createdDriver) throw new Error('No driver returned');

            await updateDriver({
              variables: {
                driverId: createdDriver.id,
                userInput: { active: true },
              },
              errorPolicy: 'all',
            });

            setRows(prev =>
              prev.map(r => (r.index === idx ? { ...r, status: 'success', driverId: createdDriver.id } : r))
            );
          } catch (error: any) {
            markError(formatDriverImportError(error));
          }
        })();

        return Promise.race([work, timeoutPromise]);
      })
    );

    setIsProcessing(false);
    setIsComplete(true);
  };

  const reset = () => {
    setRows([]);
    setIsProcessing(false);
    setIsComplete(false);
  };

  const validCount = rows.filter(r => r.status === 'pending').length;
  const successCount = rows.filter(r => r.status === 'success').length;
  const errorCount = rows.filter(r => r.status === 'error').length;

  return { rows, isProcessing, isComplete, parseCsv, processAll, reset, validCount, successCount, errorCount };
};

export default useDriverImport;
